import { ChangeDetectionStrategy, Component, computed, effect, output, signal, untracked } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatExpansionModule } from '@angular/material/expansion';
import { ButtonComponent } from '@examdojo/core/button';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { IconComponent } from '@examdojo/core/icon';
import { TextInputComponent } from '@examdojo/core/input';
import type { EChartsOption } from 'echarts';
import { ScatterChart } from 'echarts/charts';
import { GridComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';
import { v4 as uuid } from 'uuid';

echarts.use([ScatterChart, CanvasRenderer, GridComponent]);

import type { DataInputConfig } from '../../../widgets/model';
import {
  DECIMAL_PLACES_OPTIONS,
  DEFAULT_CORRELATION_CONFIG,
  DIRECTION_OPTIONS,
  PEARSON_PRESETS,
  R_SQUARED_PRESETS,
  RELATIONSHIP_CARDS,
  SAMPLE_SIZE_PRESETS,
  hasDirection,
  usesRSquared,
  type CurveDirection,
  type RelationshipType,
} from './correlation-generator.model';
import { generateCorrelatedData } from './correlation-distributions.util';
import { type CorrelationLibraryDataset, CORRELATION_LIBRARY_DATASETS } from './correlation-library-datasets.model';

@Component({
  selector: 'dojo-correlation-generator',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatButtonToggleModule,
    MatExpansionModule,
    ButtonComponent,
    IconButtonComponent,
    IconComponent,
    TextInputComponent,
    NgxEchartsDirective,
  ],
  providers: [provideEchartsCore({ echarts })],
  templateUrl: './correlation-generator.component.html',
  styleUrl: './correlation-generator.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CorrelationGeneratorComponent {
  constructor(private readonly sanitizer: DomSanitizer) {}

  readonly generate = output<DataInputConfig>();
  readonly closePanel = output<void>();

  // Library
  readonly libraryExpanded = signal(true);
  readonly customExpanded = signal(false);
  readonly libraryDatasets = CORRELATION_LIBRARY_DATASETS;

  // Relationship type
  readonly relationshipType = signal<RelationshipType>(DEFAULT_CORRELATION_CONFIG.relationshipType);
  readonly relationshipCards = RELATIONSHIP_CARDS.map((card) => ({
    ...card,
    safeSubtitleHtml: this.sanitizer.bypassSecurityTrustHtml(card.subtitleHtml),
  }));

  // Target strength: Pearson r for linear, R² for non-linear
  readonly targetStrength = signal(DEFAULT_CORRELATION_CONFIG.targetStrength);

  // Direction for non-linear types
  readonly curveDirection = signal<CurveDirection>(DEFAULT_CORRELATION_CONFIG.curveDirection);

  // Computed helpers
  readonly isRSquaredMode = computed(() => usesRSquared(this.relationshipType()));
  readonly showDirection = computed(() => hasDirection(this.relationshipType()));
  readonly directionOptions = computed(() => DIRECTION_OPTIONS[this.relationshipType()] ?? []);
  readonly strengthPresets = computed(() => (this.isRSquaredMode() ? [...R_SQUARED_PRESETS] : [...PEARSON_PRESETS]));

  // Reset strength to appropriate default when switching types
  private readonly resetStrengthEffect = effect(() => {
    const type = this.relationshipType();
    untracked(() => {
      if (usesRSquared(type)) {
        // Clamp to 0–1 range for R²
        const current = this.targetStrength();
        if (current < 0) {
          this.targetStrength.set(Math.abs(current));
        }
      }
    });
  });

  // Sample size
  readonly sampleSize = signal(DEFAULT_CORRELATION_CONFIG.sampleSize);
  readonly sampleSizePresets = SAMPLE_SIZE_PRESETS;

  // Labels
  readonly xLabelCtrl = new FormControl<string>(DEFAULT_CORRELATION_CONFIG.xLabel, {
    nonNullable: true,
  });
  readonly yLabelCtrl = new FormControl<string>(DEFAULT_CORRELATION_CONFIG.yLabel, {
    nonNullable: true,
  });

  // Decimal places
  readonly decimalPlaces = signal(DEFAULT_CORRELATION_CONFIG.decimalPlaces);
  readonly decimalPlacesOptions = DECIMAL_PLACES_OPTIONS;

  // Slider progress (for sample size: 5–200 range)
  readonly sampleSliderProgress = computed(() => {
    const val = this.sampleSize();
    return `${Math.round(((val - 5) / 195) * 100)}%`;
  });

  // Strength slider progress
  readonly strengthSliderProgress = computed(() => {
    const val = this.targetStrength();
    if (this.isRSquaredMode()) {
      return `${Math.round(val * 100)}%`;
    }
    // Linear: -1 to +1 → 0% to 100%
    return `${Math.round(((val + 1) / 2) * 100)}%`;
  });

  // Slider label
  readonly strengthLabel = computed(() => {
    if (this.isRSquaredMode()) {
      return 'TARGET FIT (R\u00B2)';
    }
    return 'TARGET CORRELATION (r)';
  });

  // Display value for the strength metric
  readonly displayStrength = computed(() => {
    const val = this.targetStrength();
    if (this.isRSquaredMode()) {
      return val.toFixed(2);
    }
    const sign = val >= 0 ? '+' : '';
    return `${sign}${val.toFixed(2)}`;
  });

  // Generate button label
  readonly generateLabel = computed(() => `Generate Correlated Pair (n=${this.sampleSize()})`);

  // ---------------------------------------------------------------------------
  // Preview
  // ---------------------------------------------------------------------------

  readonly previewData = computed(() => {
    const strength = this.targetStrength();
    const type = this.relationshipType();
    const dir = this.curveDirection();

    const pair = generateCorrelatedData({
      targetStrength: strength,
      relationshipType: type,
      curveDirection: dir,
      sampleSize: 30,
      xLabel: 'x',
      yLabel: 'y',
      decimalPlaces: 1,
      seed: `preview-${strength}-${type}-${dir}`,
    });

    return { pair };
  });

  readonly previewChartOptions = computed<EChartsOption>(() => {
    const { pair } = this.previewData();
    const data = pair.xValues.map((x, i) => [x, pair.yValues[i]]);

    return {
      grid: { left: 8, right: 8, top: 8, bottom: 8, containLabel: false },
      xAxis: { type: 'value', show: false },
      yAxis: { type: 'value', show: false },
      series: [
        {
          type: 'scatter',
          data,
          symbolSize: 6,
          itemStyle: {
            color: '#0ea5e9',
            opacity: 0.85,
          },
        },
      ],
      animation: false,
    };
  });

  readonly previewMetricLabel = computed(() => {
    if (this.isRSquaredMode()) {
      return 'R\u00B2';
    }
    return 'r';
  });

  readonly previewMetricValue = computed(() => {
    const val = this.targetStrength();
    if (this.isRSquaredMode()) {
      return val.toFixed(2);
    }
    const sign = val >= 0 ? '+' : '';
    return `${sign}${val.toFixed(2)}`;
  });

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  setTargetStrength(value: number): void {
    this.targetStrength.set(value);
  }

  onStrengthSlider(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.targetStrength.set(Number(target.value));
  }

  selectRelationshipType(type: RelationshipType): void {
    this.relationshipType.set(type);
    // Reset strength to a sensible default when switching modes
    if (usesRSquared(type)) {
      this.targetStrength.set(0.7);
      this.curveDirection.set('positive');
    } else {
      this.targetStrength.set(0.75);
    }
  }

  setCurveDirection(dir: CurveDirection): void {
    this.curveDirection.set(dir);
  }

  onSampleSizeSlider(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.sampleSize.set(Number(target.value));
  }

  setSampleSize(n: number): void {
    this.sampleSize.set(n);
  }

  setDecimalPlaces(dp: number): void {
    this.decimalPlaces.set(dp);
  }

  onLoadDataset(dataset: CorrelationLibraryDataset): void {
    const xColId = uuid();
    const yColId = uuid();
    const n = Math.min(dataset.xValues.length, dataset.yValues.length);

    const config: DataInputConfig = {
      columns: [
        { id: xColId, header: dataset.xLabel, colType: 'number' },
        { id: yColId, header: dataset.yLabel, colType: 'number' },
      ],
      rows: Array.from({ length: n }, (_, i) => ({
        [xColId]: dataset.xValues[i],
        [yColId]: dataset.yValues[i],
      })),
      rowCount: n,
      editableHeaders: true,
      allowAddColumns: false,
      allowAddRows: true,
    };

    this.generate.emit(config);
  }

  onGenerate(): void {
    const xColId = uuid();
    const yColId = uuid();
    const xLabel = this.xLabelCtrl.value || 'x';
    const yLabel = this.yLabelCtrl.value || 'y';

    const pair = generateCorrelatedData({
      targetStrength: this.targetStrength(),
      relationshipType: this.relationshipType(),
      curveDirection: this.curveDirection(),
      sampleSize: this.sampleSize(),
      xLabel,
      yLabel,
      decimalPlaces: this.decimalPlaces(),
      seed: '',
    });

    const n = pair.xValues.length;
    const config: DataInputConfig = {
      columns: [
        { id: xColId, header: xLabel, colType: 'number' },
        { id: yColId, header: yLabel, colType: 'number' },
      ],
      rows: Array.from({ length: n }, (_, i) => ({
        [xColId]: pair.xValues[i],
        [yColId]: pair.yValues[i],
      })),
      rowCount: n,
      editableHeaders: true,
      allowAddColumns: false,
      allowAddRows: true,
    };

    this.generate.emit(config);
  }
}
